import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import { Separator } from "../components/ui/separator";

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen" data-testid="privacy-policy-page">
      <Navigation />
      
      <div className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-medium mb-4">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8">Last updated: January 2025</p>
          
          <div className="prose prose-lg dark:prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-medium mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                Art By Arawol ("we", "our", "us") is committed to protecting your privacy in accordance with the 
                Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs). This Privacy Policy explains 
                how we collect, use, disclose, and safeguard your personal information when you visit our website 
                or make a purchase.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We are based in New South Wales, Australia, and operate under Australian law including the 
                Australian Consumer Law (ACL).
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">2. Information We Collect</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">We may collect the following types of personal information:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Identity Information:</strong> Name, email address, phone number</li>
                <li><strong>Contact Information:</strong> Billing address, shipping address</li>
                <li><strong>Transaction Information:</strong> Purchase history, payment details (processed securely via Stripe/PayPal)</li>
                <li><strong>Technical Information:</strong> IP address, browser type, device information</li>
                <li><strong>Usage Information:</strong> How you interact with our website</li>
                <li><strong>Marketing Preferences:</strong> Your preferences for receiving marketing communications</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">3. How We Collect Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">We collect personal information:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Directly from you when you create an account, make a purchase, or contact us</li>
                <li>Automatically through cookies and similar technologies when you browse our website</li>
                <li>From third-party services such as Google (if you use social login)</li>
                <li>From payment processors (Stripe, PayPal) for transaction verification</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">4. How We Use Your Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">We use your personal information to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Process and fulfill your orders</li>
                <li>Provide Certificates of Authenticity for artwork purchases</li>
                <li>Communicate with you about your orders and account</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
                <li>Prevent fraud and maintain security</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">5. Disclosure of Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li><strong>Payment Processors:</strong> Stripe and PayPal for secure payment processing</li>
                <li><strong>Shipping Partners:</strong> To deliver your purchases</li>
                <li><strong>Service Providers:</strong> Who assist us in operating our business (hosting, email services)</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                We do not sell your personal information to third parties.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">6. Cookies and Tracking</h2>
              <p className="text-muted-foreground leading-relaxed">
                We use cookies and similar technologies to enhance your browsing experience, remember your 
                preferences, and analyze website traffic. You can control cookies through your browser settings, 
                but disabling them may affect website functionality.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">7. Data Security</h2>
              <p className="text-muted-foreground leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal information 
                against unauthorized access, alteration, disclosure, or destruction. Payment information is 
                processed through PCI-DSS compliant payment processors and is never stored on our servers.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">8. Your Rights</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">Under Australian Privacy Law, you have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information (subject to legal requirements)</li>
                <li>Opt-out of marketing communications at any time</li>
                <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC)</li>
              </ul>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">9. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined 
                in this policy, unless a longer retention period is required by law. Transaction records are 
                retained for 7 years as required by Australian tax law.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">10. International Transfers</h2>
              <p className="text-muted-foreground leading-relaxed">
                Some of our service providers may be located overseas. When we transfer personal information 
                overseas, we take reasonable steps to ensure the recipient handles your information in accordance 
                with the Australian Privacy Principles.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">11. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes 
                by posting the new policy on our website and updating the "Last updated" date.
              </p>
            </section>

            <Separator />

            <section>
              <h2 className="text-2xl font-medium mb-4">12. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about this Privacy Policy or wish to exercise your rights, please contact us:
              </p>
              <div className="mt-4 p-4 bg-card rounded-xl border border-border">
                <p className="text-muted-foreground">
                  <strong>Art By Arawol</strong><br />
                  Email: privacy@arawol.art<br />
                  New South Wales, Australia
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
